import { lazy, Suspense, useEffect, useState } from "react";
import { LabelCtx, LoadingOverlay, ThemeCtx, usePeriodRange } from "./components/ui";
import DashboardShell from "./components/DashboardShell";
import { PageBoundary, PageFallback } from "./components/PageBoundary";
import { TABS } from "./constants";
import { DARK, LIGHT } from "./theme";
import { useApps } from "./hooks/useApps";
import { useLocalStorageState } from "./hooks/useLocalStorageState";
import { hasDashboardFilterChanges, isDashboardTargetValid } from "./dashboardFilters";
import { apiFetch } from "./api";

const LOCAL_TOKEN = "local";
const LOCAL_USER = { username: "local", role: "admin" };
const Overview = lazy(() => import("./components/Overview"));
const AdRevenue = lazy(() => import("./components/AdRevenue"));
const GA4 = lazy(() => import("./components/GA4"));
const IAP = lazy(() => import("./components/IAP"));
const Admin = lazy(() => import("./components/Admin"));

export default function App() {
  const [activeTab, setActiveTab] = useState("overview");
  const [isDark, setIsDark] = useLocalStorageState("dashboard-theme-dark", false);
  const [showLabels, setShowLabels] = useLocalStorageState("dashboard-chart-labels", false);
  const [labelSize, setLabelSize] = useLocalStorageState("dashboard-chart-label-size", 11);
  const [compareMode, setCompareMode] = useState(false);
  const [slotA, setSlotA] = useState({ appId: null, allApps: false, platform: "" });
  const [slotB, setSlotB] = useState({ appId: null, allApps: false, platform: "" });
  const [appliedCompareMode, setAppliedCompareMode] = useState(false);
  const [appliedSlotA, setAppliedSlotA] = useState({ appId: null, allApps: false, platform: "" });
  const [appliedSlotB, setAppliedSlotB] = useState({ appId: null, allApps: false, platform: "" });
  const { apps, setApps, visibleApps, error: appsError, refresh: refreshApps } = useApps(LOCAL_TOKEN);
  const period = usePeriodRange();
  const setPeriodRefDate = period.setRefDate;

  useEffect(() => {
    const visibleIds = new Set(visibleApps.map(app => app.id));
    const emptySlot = { appId: null, allApps: false, platform: "" };
    setSlotA(current => current.appId && !visibleIds.has(current.appId) ? emptySlot : current);
    setSlotB(current => current.appId && !visibleIds.has(current.appId) ? emptySlot : current);
    setAppliedSlotA(current => current.appId && !visibleIds.has(current.appId) ? emptySlot : current);
    setAppliedSlotB(current => current.appId && !visibleIds.has(current.appId) ? emptySlot : current);
  }, [visibleApps]);

  useEffect(() => {
    if (activeTab !== "iap" || !slotA.appId) return;
    let active = true;
    apiFetch(`/api/apps/${slotA.appId}/iap/date-range`, { silent: true }, LOCAL_TOKEN)
      .then(data => { if (active && data.max_date) setPeriodRefDate(data.max_date); })
      .catch(() => {});
    return () => { active = false; };
  }, [activeTab, slotA.appId, setPeriodRefDate]);

  const handleTabChange = nextTab => {
    setActiveTab(nextTab);
    window.scrollTo(0, 0);
    window.requestAnimationFrame(() => window.scrollTo(0, 0));
  };

  const theme = isDark ? DARK : LIGHT;
  const visibleTabs = TABS.filter(tab => !tab.adminOnly || LOCAL_USER.role === "admin");
  const targetPending = hasDashboardFilterChanges(
    { slotA, slotB, compareMode },
    { slotA: appliedSlotA, slotB: appliedSlotB, compareMode: appliedCompareMode },
  );
  const hasPendingAll = targetPending || period.hasPending;
  const targetValid = isDashboardTargetValid({
    slotA,
    slotB,
    compareMode,
    allowAllApps: activeTab === "overview",
  });
  const canApplyAll = targetValid
    && period.validDraftA
    && (compareMode || !period.draftPeriodCompare || period.validDraftB);
  const applyAll = () => {
    if (!canApplyAll || !period.applyDraft(compareMode)) return;
    setAppliedSlotA({ ...slotA });
    setAppliedSlotB({ ...slotB });
    setAppliedCompareMode(compareMode);
  };
  const pagePeriod = {
    ...period,
    applyAll,
    hasPendingAll,
    canApplyAll,
    draftTargetCompareMode: compareMode,
  };
  const pageProps = {
    apps: visibleApps,
    token: LOCAL_TOKEN,
    slotA: appliedSlotA,
    slotB: appliedSlotB,
    compareMode: appliedCompareMode,
    pr: pagePeriod,
  };

  return (
    <ThemeCtx.Provider value={theme}>
      <LabelCtx.Provider value={{ show: showLabels, size: labelSize }}>
        <LoadingOverlay />
        <DashboardShell
          tabs={visibleTabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          user={LOCAL_USER}
          apps={visibleApps}
          token={LOCAL_TOKEN}
          slotA={slotA}
          setSlotA={setSlotA}
          slotB={slotB}
          setSlotB={setSlotB}
          compareMode={compareMode}
          setCompareMode={setCompareMode}
          isDark={isDark}
          setIsDark={setIsDark}
          showLabels={showLabels}
          setShowLabels={setShowLabels}
          labelSize={labelSize}
          setLabelSize={setLabelSize}
          appsError={appsError}
          onRetryApps={refreshApps}
        >
          <PageBoundary pageKey={activeTab}>
            <Suspense fallback={<PageFallback />}>
              {activeTab === "overview" && <Overview {...pageProps} />}
              {activeTab === "ad" && <AdRevenue {...pageProps} />}
              {activeTab === "ga4" && <GA4 {...pageProps} />}
              {activeTab === "iap" && <IAP {...pageProps} />}
              {activeTab === "admin" && <Admin apps={apps} onAppsChange={setApps} token={LOCAL_TOKEN} />}
            </Suspense>
          </PageBoundary>
        </DashboardShell>
      </LabelCtx.Provider>
    </ThemeCtx.Provider>
  );
}
